import {
  Box,
  Flex,
  Heading,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  theme,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { BankDetailStatus } from '@services/appsync/types';
import React, { useEffect, useState } from 'react';
import PlaidLink from 'src/apps/invest/common/components/PlaidLink';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { UI } from 'src/apps/invest/common/shells/UI';
import { useBankAccountsModel } from '../../../../../common/models/BankAccountsModel';
import { VerifyAccountButton } from 'src/apps/invest/common/components/VerifyAccountButton';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';

export const BankAccountsPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const model = useBankAccountsModel();
  useEffect(() => {
    const fetchBankAccounts = async () => {
      setIsLoading(true);
      model.fetchBankAccounts();
      setIsLoading(false);
    };
    fetchBankAccounts();
  }, []);

  const onSuccess = async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    setIsLoading(true);
    const status = await model.addBankAccount(publicToken, metadata);
    if (!status.success) {
      alert(status.error);
    }
    setIsLoading(false);
  };

  return (
    <UI>
      {isLoading ? (
        <VStack>
          <Spinner color="blue.500" />
        </VStack>
      ) : (
        <Flex margin={'0px auto'} w="full">
          <Box margin={'auto'} w="full">
            <Flex justifyContent={'space-between'} pb={'24px'}>
              <Heading fontSize={'24px'}>Bank Accounts</Heading>
              <PlaidLink
                onSuccess={onSuccess}
                buttonProps={{
                  children: 'Add Bank Account',
                  colorScheme: "blue",
                  size: 'sm',
                }}
              />
            </Flex>
            <VStack gap="10px" w="full">
              {model?.bankAccounts.map((bankAccount, i) => {
                return (
                  <Flex
                    border={`1px solid ${theme.colors.gray[200]}`}
                    borderRadius={'10px'}
                    bg={'white'}
                    flexDirection={['column', 'row']}
                    justifyContent={'space-between'}
                    alignItems={['center']}
                    key={`bankAccount-${i}`}
                    maxWidth={'100vw'}
                    w="full">
                    <Box borderBottomWidth={'0px'} p={'12px 24px'} pr={0}>
                      <VStack align={['center', 'flex-start']}>
                        <TextBlock weight="bold">{bankAccount.name}</TextBlock>
                        {bankAccount.status === BankDetailStatus.PENDING_MANUAL_VERIFICATION ? (
                          <Popover>
                            <PopoverTrigger>
                              <HStack cursor={'pointer'} alignItems={'center'} justifyContent={'center'} opacity="0.7">
                                <Icon icon="circle-info" variant="regular" color="red.300" />
                                <TextBlock>Verification needed</TextBlock>
                              </HStack>
                            </PopoverTrigger>
                            <PopoverContent>
                              <PopoverArrow />
                              <PopoverCloseButton />
                              <PopoverHeader>Verifying Your Account</PopoverHeader>
                              <PopoverBody color="blackAlpha.500" padding="40px">
                                This account was added manually and must be verified with a code. Look for a $0.01
                                deposit in your account ending in ...1111. Your code is the first 3 letters after the #.
                                Once you receive your code (1-2 days), use the Verify button to enter it.
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <TextBlock>{`...${bankAccount.account_number ?? ''}`}</TextBlock>
                        )}
                      </VStack>
                    </Box>
                    <HStack justify="flex-end" spacing={2} p={'15px'}>
                      {bankAccount.id && bankAccount.status === BankDetailStatus.PENDING_MANUAL_VERIFICATION ? (
                        <VerifyAccountButton accountID={bankAccount.id} />
                      ) : (
                        <IconButton
                          onClick={async () => {
                            if (bankAccount.id && !bankAccount.default) {
                              setIsLoading(true);
                              const status = await model.setDefaultBankAccount(bankAccount.id);
                              if (!status.success) alert(status.error);
                              setIsLoading(false);
                            }
                          }}
                          variant="unstyled"
                          icon={
                            <Icon
                              variant={bankAccount.default ? 'solid' : 'regular'}
                              fontSize={'xl'}
                              color={bankAccount.default ? 'yellow.500' : 'gray'}
                              icon="star"></Icon>
                          }
                          aria-label="favorite"></IconButton>
                      )}
                      <Tooltip label={bankAccount.default ? 'Can not delete default bank account' : null}>
                        <IconButton
                          isDisabled={!!bankAccount.default}
                          onClick={async () => {
                            if (confirm(`Are you sure you want to delete ${bankAccount.name}?`)) {
                              setIsLoading(true);
                              if (!bankAccount.id) return;
                              const status = await model.deleteBankAccount(bankAccount.id);
                              if (!status.success) alert(status.error);
                              setIsLoading(false);
                            }
                          }}
                          variant="unstyled"
                          color={'gray.200'}
                          icon={<Icon icon="trash"></Icon>}
                          _hover={{
                            color: 'blue.500',
                          }}
                          aria-label="delete"
                        />
                      </Tooltip>
                    </HStack>
                  </Flex>
                );
              })}
            </VStack>
          </Box>
        </Flex>
      )}
    </UI>
  );
};
